import React from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import { DateTime } from "luxon";
import CustomApprovalModal from "@/shared/components/custom-modal";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import MembershipInfoBox from "@/features/pathpick/features/membership/components/MembershipInfoBox";
import useGetPathPickMembershipApplications from "@/features/pathpick/features/membership/hooks/query/useGetPathPickMembershipApplications";
import { MEMBERSHIP_BANK_ACCOUNT } from "@/features/pathpick/features/membership/constants";
import { FORMAT_DOT_HM } from "@/shared/constants/date";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface InfoFieldProps {
  label: string;
  value?: string | null;
}

// 신청 내용 행 — 응답 필드가 전부 nullable이라 "-" 폴백을 컴포넌트에 내장한다.
// 같은 모양이 주문 상세/주문 완료 모달에도 있지만, 하위 feature를 끌어오지 않으려고
// 각자 두는 게 이 코드베이스의 관례라 여기서도 로컬로 둔다.
function InfoField({ label, value }: InfoFieldProps) {
  return (
    <Flex flexDirection={"column"} gap={"2px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {value || "-"}
      </Text>
    </Flex>
  );
}

// 입금 대기 중인 멤버십 신청 요약 — 입금 계좌와 접수된 신청 내용을 함께 보여준다.
// 두 경로가 띄운다: (1) 대기 중에 다시 "플랜 시작하기"를 누른 경우, (2) 방금 신청을 끝낸 직후.
// 신청 폼(PathPickMembershipPlanChangeModal)을 잠가서 재활용하지 않는 이유는,
// 폼 기본값이 실제 접수 값과 달라 신청 정보처럼 오해를 사기 때문이다.
function PathPickMembershipPendingApplicationModal({ isOpen, onClose }: Props) {
  const { t, tNumberString } = useI18nHelper();
  const { pendingApplication, isFetching } =
    useGetPathPickMembershipApplications();

  // 신청 직후 재조회가 끝나기 전에는 빈 표 대신 로딩을 보여준다.
  // 조회가 끝난 뒤에는 대기 건이 없어도 로딩에 머무르지 않는다 — 모달이 떠 있는 동안
  // 어드민이 입금을 확인해 버리면 대기 건이 사라지는데, 그때 무한 스피너가 되면 안 된다.
  // (그 경우 값은 InfoField의 "-" 폴백으로 떨어진다)
  const isLoading = isFetching && pendingApplication === undefined;

  const amount = pendingApplication?.amount;
  const plan = pendingApplication?.plan;
  const appliedAt = pendingApplication?.appliedAt;

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 가운데 정렬된 모달은 화면보다 넓으면 왼쪽이 잘려 스크롤로도 닿을 수 없다.
      minWidth={"min(560px, 90dvw)"}
      scrollBehavior={"inside"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("membership:pending_modal.title")}
      />
      <CustomApprovalModal.Body>
        {isLoading ? (
          <CenteredSpinner minHeight={"200px"} />
        ) : (
          <Flex flexDirection={"column"} gap={"12px"}>
            <PathPickInlineNotice
              tone={"yellow"}
              leadingIcon={"warning-triangle"}
            >
              {t("membership:pending_modal.notice")}
            </PathPickInlineNotice>

            {/* 입금 계좌 — 신청 모달과 같은 3칸 구성.
                좁은 폭에서 3열이면 칸당 ~105px이라 계좌번호가 깨진다. */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={"12px"}
            >
              <MembershipInfoBox
                label={t("membership:change_modal.bank")}
                value={MEMBERSHIP_BANK_ACCOUNT.bankName}
              />
              <MembershipInfoBox
                label={t("membership:change_modal.account_number")}
                value={MEMBERSHIP_BANK_ACCOUNT.accountNumber}
              />
              <MembershipInfoBox
                label={t("membership:change_modal.account_holder")}
                value={MEMBERSHIP_BANK_ACCOUNT.accountHolder}
              />
            </Grid>

            {/* 접수된 신청 내용 — 프런트 상수로 다시 계산하지 않고 응답 값을 그대로 쓴다.
                금액은 프로모션·연 할인이 이미 반영된 실제 청구액이다. */}
            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={"16px"}
              padding={"16px"}
              borderRadius={"8px"}
              border={"0.5px solid #D4D4D1"}
              background={"#F7F9FA"}
            >
              <Flex flexDirection={"column"} gap={"2px"}>
                <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                  {t("membership:pending_modal.amount")}
                </Text>
                <Text textStyle={"body-lg-bold"} color={"#00417D"}>
                  {amount != null
                    ? t("membership:price.won", {
                        amount: tNumberString(amount),
                      })
                    : "-"}
                </Text>
              </Flex>
              <InfoField
                label={t("membership:pending_modal.plan")}
                value={plan?.name ?? plan?.code}
              />
              <InfoField
                label={t("membership:pending_modal.billing_cycle")}
                value={t(
                  pendingApplication?.billingCycle === "YEARLY"
                    ? "membership:billing.yearly"
                    : "membership:billing.monthly"
                )}
              />
              <InfoField
                label={t("membership:change_modal.depositor_label")}
                value={pendingApplication?.remitterName}
              />
              <InfoField
                label={t("membership:change_modal.tax_email_label")}
                value={pendingApplication?.taxInvoiceEmail}
              />
              <InfoField
                label={t("membership:pending_modal.applied_at")}
                value={
                  appliedAt
                    ? DateTime.fromISO(appliedAt).toFormat(FORMAT_DOT_HM)
                    : null
                }
              />
            </Grid>
          </Flex>
        )}
      </CustomApprovalModal.Body>
      {/* onConfirm을 넘기지 않으면 Footer가 확인 버튼을 렌더하지 않아 닫기만 남는다. */}
      <CustomApprovalModal.Footer
        cancelName={t("membership:change_modal.close")}
        onCancel={onClose}
      />
    </CustomApprovalModal>
  );
}

export default PathPickMembershipPendingApplicationModal;
